import { forwardRef, useEffect, useState } from 'react';

import type { DefaultComponentProps } from '../../types';

import {
  CharacterCount,
  Container,
  Label,
  TextareaElement,
} from './Textarea.styled';

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    DefaultComponentProps {
  textareaRef?: React.Ref<HTMLTextAreaElement>;
  label?: string;
  maxLength?: number;
  error?: string;
}

const Textarea = ({
  className,
  'data-testid': dataTestId,
  textareaRef,
  label,
  maxLength,
  error,
  value,
  ...rest
}: TextareaProps) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!count && value && typeof value !== 'number') {
      setCount(value.length);
    }
  }, [value, count]);

  const handleValueChange = (event: React.ChangeEvent<HTMLLabelElement>) => {
    if (event.target instanceof HTMLTextAreaElement) {
      const inputValue = event.target.value;

      if (maxLength) {
        if (inputValue.length > maxLength) {
          const trimmedValue = inputValue.slice(0, maxLength);

          event.target.value = trimmedValue;
          setCount(trimmedValue.length);
        } else {
          setCount(inputValue.length);
        }
      }
    }
  };

  return (
    <Container
      className={className}
      data-testid={dataTestId}
      onChange={handleValueChange}>
      {label && <Label $hasError={!!error}>{label}</Label>}
      <TextareaElement
        $hasError={!!error}
        cols={50}
        rows={4}
        {...rest}
        ref={textareaRef}
      />

      {maxLength && (
        <CharacterCount>{`${count} / ${maxLength}`}</CharacterCount>
      )}
    </Container>
  );
};

export default forwardRef<HTMLTextAreaElement, TextareaProps>((props, ref) => (
  <Textarea {...props} textareaRef={ref} />
));
